import {
  fetchPerformanceDetailIndustryApi,
  fetchPerformanceDetailMatchingApi,
  fetchPerformanceDetailStaffApi,
  fetchPerformanceDetailSummaryApi,
} from "@/api/services/main/dashboard";
import { TAnnualSummary, TMonthlyRow } from "@/api/services/main/dashboard/dtos/performance-detail.dto";
import { convertCSVDataToDownloadUrlWithShiftJIS, downloadFileByUrl } from "@/utils/export";
import { Dayjs } from "dayjs";
import { CONVERSION_RATE_LABELS } from "@/features/dashboard/constants/conversion-rate-labels";

type TExportMonthlyStatisticsCsvParams = {
  // Selected month from the dashboard filter.
  targetMonth: Dayjs;
};


/** Escape a CSV field per RFC 4180 */
const escapeCsv = (v: string): string => {
  if (v.includes(",") || v.includes('"') || v.includes("\n")) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
};

/** Join values as CSV row, pad to fixed column count */
const csvRow = (values: string[], totalCols: number): string => {
  const padded = [...values];
  while (padded.length < totalCols) padded.push("");
  return padded.map(escapeCsv).join(",");
};

const emptyRow = (cols: number) => csvRow([], cols);

/** Format scalar as string; empty for null/undefined */
const fmt = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined) return "";
  return String(v);
};

/** Format previous-month delta like "↑ +3 先月比" / "↓ -2 先月比" / "" when no change */
const fmtMoM = (current: number, previous: number): string => {
  const diff = current - previous;
  if (diff === 0) return "";
  const arrow = diff > 0 ? "↑" : "↓";
  const sign = diff > 0 ? "+" : "";
  return `${arrow} ${sign}${diff} 先月比`;
};

/**
 * Slice a 12-element fiscal-year months array to [prevMonth, currentMonth] for the dashboard's
 * 2-month view. The performance-detail endpoint returns months ordered by fiscal-year start
 * (decided by the request's targetMonth = closing month). We pass `targetMonth` as the selected
 * month so its index in the returned array is always 11 (last position), and prev is index 10.
 */
const sliceLastTwoMonths = (months: (number | string | null)[]): (number | string | null)[] => {
  const len = months.length;
  if (len < 2) return [null, null];
  return [months[len - 2], months[len - 1]];
};

/** Sum a column across rows (only numeric values); null if no numeric values present */
const sumColumn = (rows: TMonthlyRow[], colIndex: number): number | null => {
  let sum = 0;
  let hasValue = false;
  for (const row of rows) {
    const v = colIndex === -1 ? row.total : row.months[colIndex];
    if (typeof v === "number") {
      sum += v;
      hasValue = true;
    }
  }
  return hasValue ? sum : null;
};

/** Recompute total from the 2-month slice (sum of numbers only) */
const sumTwoMonth = (slice: (number | string | null)[]): number | string | null => {
  let sum = 0;
  let hasValue = false;
  for (const v of slice) {
    if (typeof v === "number") {
      sum += v;
      hasValue = true;
    }
  }
  return hasValue ? sum : null;
};

/**
 * Build and download the dashboard monthly statistics CSV (mirrors sample.csv template).
 * Charset: Shift-JIS for Japanese Excel compatibility.
 *
 * Reuses the 4 performance-detail endpoints with `targetYear` = year of selected month and
 * `targetMonth` = selected month (treated as fiscal-year closing month so the returned array
 * ends at the selected month). Only the last 2 columns (先月 + 今月) plus 合計 are emitted.
 */
export const exportMonthlyStatisticsCsv = async (params: TExportMonthlyStatisticsCsvParams) => {
  const { targetMonth } = params;
  const targetYear = targetMonth.format("YYYY");
  const monthNum = targetMonth.month() + 1; // 1..12
  const apiParams = { targetYear, targetMonth: String(monthNum) };

  // Fetch all 4 datasets in parallel (lazy — only on click, no extra requests on render).
  // Staff/industry: pass `status=-1` (sentinel) → BE returns all-status aggregate
  // (sum of events across PROPOSING/UNDER_SELECTION/OFFER/OFFER_ACCEPTED date fields).
  const STATUS_ALL = "-1";
  const [summaryRes, matchingRes, staffRes, industryRes] = await Promise.all([
    fetchPerformanceDetailSummaryApi(apiParams),
    fetchPerformanceDetailMatchingApi(apiParams),
    fetchPerformanceDetailStaffApi({ ...apiParams, status: STATUS_ALL }),
    fetchPerformanceDetailIndustryApi({ ...apiParams, status: STATUS_ALL }),
  ]);

  const summary: TAnnualSummary | undefined = summaryRes?.data;
  const matchingRows: TMonthlyRow[] = matchingRes?.data?.rows ?? [];
  const staffRows: TMonthlyRow[] = staffRes?.data?.rows ?? [];
  const industryRows: TMonthlyRow[] = industryRes?.data?.rows ?? [];

  const prevMonthNum = ((monthNum - 2 + 12) % 12) + 1;
  const monthHeaders = [`${prevMonthNum}月`, `${monthNum}月`];
  const totalCols = 12;
  const lines: string[] = [];

  // --- Section 1: 指定年月の実績値 + summary cards ---
  lines.push(csvRow(["指定年月の実績値", ...Array(8).fill(""), `対象年度: ${targetYear}　　決算月: ${monthNum}月`], totalCols));
  lines.push(emptyRow(totalCols));

  if (summary) {
    // Top cards: 新規求人数 / 新規登録人材数 (uses *PrevYear fields as previous-month proxy from API)
    lines.push(csvRow(["新規求人数", ...Array(6).fill(""), "新規登録人材数"], totalCols));
    lines.push(
      csvRow(
        [
          `${fmt(summary.newJobs)} 件`,
          ...Array(3).fill(""),
          fmtMoM(summary.newJobs, summary.newJobsPrevYear),
          ...Array(2).fill(""),
          `${fmt(summary.newTalents)} 人`,
          ...Array(3).fill(""),
          fmtMoM(summary.newTalents, summary.newTalentsPrevYear),
        ],
        totalCols,
      ),
    );
    lines.push(emptyRow(totalCols));

    // Bottom cards: 5 funnel stages (推薦前数 added by DEV-436), each taking 2 columns: value + 前月比.
    const funnelStages = [
      { label: "推薦前数", value: summary.beforeProposals, prevValue: summary.beforeProposalsPrevYear },
      { label: "推薦数", value: summary.proposals, prevValue: summary.proposalsPrevYear },
      { label: "面接数", value: summary.interviews, prevValue: summary.interviewsPrevYear },
      { label: "内定数", value: summary.offers, prevValue: summary.offersPrevYear },
      { label: "内定受諾数", value: summary.offerAccepted, prevValue: summary.offerAcceptedPrevYear },
    ];
    lines.push(
      csvRow(
        funnelStages.flatMap((stage) => [stage.label, ""]),
        totalCols,
      ),
    );
    lines.push(
      csvRow(
        funnelStages.flatMap((stage) => [`${fmt(stage.value)} 件`, fmtMoM(stage.value, stage.prevValue)]),
        totalCols,
      ),
    );
    lines.push(emptyRow(totalCols));
  }

  // --- Section 2: 月間マッチング数 (conversion-rate rows always included) ---
  lines.push(emptyRow(totalCols));
  lines.push(csvRow([`月間マッチング数（${targetYear}年${monthNum}月）`], totalCols));
  lines.push(csvRow(["", ...monthHeaders, "合計"], totalCols));
  for (const row of matchingRows) {
    const slice = sliceLastTwoMonths(row.months);
    // Conversion rate rows display fixed total (last value), others recompute from slice.
    const isRate = CONVERSION_RATE_LABELS.includes(row.label);
    const total = isRate ? row.total : sumTwoMonth(slice);
    lines.push(csvRow([row.label, ...slice.map((v) => fmt(v)), fmt(total)], totalCols));
  }
  lines.push(emptyRow(totalCols));

  // --- Section 3: 担当者別月間実績表 ---
  lines.push(csvRow([`担当者別月間実績表（${targetYear}年${monthNum}月）`], totalCols));
  lines.push(csvRow(["担当者", ...monthHeaders, "合計"], totalCols));
  const staffSlices: (number | string | null)[][] = [];
  for (const row of staffRows) {
    const slice = sliceLastTwoMonths(row.months);
    staffSlices.push(slice);
    lines.push(csvRow([row.label, ...slice.map((v) => fmt(v)), fmt(sumTwoMonth(slice))], totalCols));
  }
  if (staffRows.length > 0) {
    // Footer totals across rows (per column) — recompute from sliced columns.
    const staffSliceRows: TMonthlyRow[] = staffRows.map((r, i) => ({ label: r.label, months: staffSlices[i], total: sumTwoMonth(staffSlices[i]) }));
    const totals = [fmt(sumColumn(staffSliceRows, 0)), fmt(sumColumn(staffSliceRows, 1)), fmt(sumColumn(staffSliceRows, -1))];
    lines.push(csvRow(["合計", ...totals], totalCols));
  }
  lines.push(emptyRow(totalCols));

  // --- Section 4: 分野別月間実績表 ---
  lines.push(csvRow([`分野別月間実績表（${targetYear}年${monthNum}月）`], totalCols));
  lines.push(csvRow(["分野", ...monthHeaders, "合計"], totalCols));
  const industrySlices: (number | string | null)[][] = [];
  for (const row of industryRows) {
    const slice = sliceLastTwoMonths(row.months);
    industrySlices.push(slice);
    lines.push(csvRow([row.label, ...slice.map((v) => fmt(v)), fmt(sumTwoMonth(slice))], totalCols));
  }
  if (industryRows.length > 0) {
    const industrySliceRows: TMonthlyRow[] = industryRows.map((r, i) => ({ label: r.label, months: industrySlices[i], total: sumTwoMonth(industrySlices[i]) }));
    const totals = [fmt(sumColumn(industrySliceRows, 0)), fmt(sumColumn(industrySliceRows, 1)), fmt(sumColumn(industrySliceRows, -1))];
    lines.push(csvRow(["合計", ...totals], totalCols));
  }

  const csvData = lines.join("\r\n");
  const downloadUrl = convertCSVDataToDownloadUrlWithShiftJIS(csvData);
  const fileName = `月間実績表（${targetYear}年${monthNum}月）.csv`;
  await downloadFileByUrl(fileName, downloadUrl);
};
